import React from 'react';
import { formatBusiness } from 'config/time';
import { useNavigate } from 'react-router-dom';
import user_logo from 'assets/icons/user.svg';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import TableScroll, { PAGE_TITLE_ID, tabId } from 'reusable/TableScroll';

interface Vehicles {
  id: string;
  plate_number: string;
  car_model: string;
  last_used_by: {
    name: string;
    phone: string;
    email: string;
    profile_picture?: string;
  };
  create_time: Date;
  last_inspection: Date;
}

interface AllVehicleTableProps {
  vehicles: Vehicles[];
  onEdit: (vehicleId: string) => void;
  onDelete: (vehicleId: string) => void;
}

const AllVehicleTable: React.FC<AllVehicleTableProps> = ({
  vehicles,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation('vehicle');
  const navigate = useNavigate();

  const checkDate = (initialDate: Date | string | null) => {
    // EV-116。端末の時間帯ではなく、業務の時間帯（日本時間）で出す（絞り込みの業務日と揃える）。
    // 未設定（null・1970-01-01）は「-」。以前は端末によって 1969/12/31 が出ていた。
    if (!initialDate || formatBusiness(initialDate, 'yyyy-MM-dd') === '1970-01-01') return '-';
    return formatBusiness(initialDate, i18n.language === 'jp' ? 'yyyy/MM/dd' : 'dd MMM yyyy');
  };

  const handleRowClick = (id: string) => {
    navigate(`/vehicle/user/${id}`);
  };

  return (
    <main className="flex flex-col my-6">
      {/* EV-309。768px などの狭い幅では、表に最小の幅を持たせて表の外枠の中だけを横に送る（EV-301 と同じ TableScroll）。
          データが無いときの文は外枠の外に置き、見える幅の中央に出す（PR #244 の Codex の指摘と同じ）。 */}
      <TableScroll labelledBy={`${PAGE_TITLE_ID} ${tabId('allvehicle')}`}>
      <div className="grid grid-cols-12 gap-4 px-2 py-3 bg-primary/50 rounded-md w-full">
        <div className="col-span-2">{t('all-vehicle.table.plate-number')}</div>
        <div className="col-span-3">{t('all-vehicle.table.car-model')}</div>
        <div className="col-span-2">{t('all-vehicle.table.last-used-by')}</div>
        <div className="col-span-2 text-center">
          {t('all-vehicle.table.added-date')}
        </div>
        <div className="col-span-2 text-center">
          {t('all-vehicle.table.last-inspection')}
        </div>
        <div className="col-span-1 text-center">
          {t('all-vehicle.table.action')}
        </div>
      </div>
      {vehicles.length === 0 ? null : (
        vehicles.map((vehicle, index) => (
          <div
            key={index}
            className="grid grid-cols-12 gap-4 px-2 py-3 border-b cursor-pointer"
            onClick={() => handleRowClick(vehicle.id)}
            // ADM-16。キーボードでは車両番号のボタンで詳細へ進む（行のクリックはマウスの近道）。
            data-pointer-shortcut
          >
            <div className="col-span-2 flex items-center text-neutral/100 truncate">
              <button
                type="button"
                className="truncate text-left hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(vehicle.id);
                }}
              >
                {vehicle.plate_number}
              </button>
            </div>
            <div className="col-span-3 flex items-center truncate">
              {vehicle.car_model}
            </div>
            {vehicle.last_used_by ? (
              <div className="col-span-2 flex space-x-2 items-center truncate">
                <img
                  src={vehicle.last_used_by?.profile_picture || user_logo}
                  alt=""
                  className="h-10 w-10 object-cover rounded-full"
                />
                {/* EV-315。表の欄の中の空は「-」（EV-311。以前は「データなし」・空白・「---」）。 */}
                <div>
                  <div className="w-32">
                    {vehicle.last_used_by?.name || '-'}
                  </div>
                  <div className="text-neutral/100 w-32">
                    {vehicle.last_used_by?.phone || vehicle.last_used_by?.email || '-'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-span-2 flex items-center">-</div>
            )}
            <div className="col-span-2 flex items-center justify-center text-center">
              {checkDate(vehicle.create_time)}
            </div>
            <div className="col-span-2 flex items-center justify-center text-center">
              {checkDate(vehicle.last_inspection)}
            </div>
            <div className="col-span-1 flex items-center justify-center gap-3 cursor-pointer text-neutral/100">
              <button
                type="button"
                className="inline-flex items-center"
                aria-label={t('ui.edit', { ns: 'common' })}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(vehicle.id);
                }}
              >
                <IconEdit
                  size={20}
                  stroke={1.5}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="inline-flex items-center"
                aria-label={t('ui.delete', { ns: 'common' })}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(vehicle.id);
                }}
              >
                <IconTrash
                  size={20}
                  stroke={1.5}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        ))
      )}
      </TableScroll>
      {vehicles.length === 0 && <div className="text-center my-4">{t('table.no-data')}</div>}
    </main>
  );
};

export default AllVehicleTable;
